<template>
  <div>
    <collapse title="Breve descripción de los servicios solicitados">
      <validation-provider
        v-if="!isReadOnly"
        v-slot="{ errors }"
        :custom-messages="{
          required: 'El campo es requerido.',
        }"
        rules="required"
      >
        <custom-text-area
          v-model="model.serviceDescription"
          :count-classes="['d-block', 'small-counter']"
          :custom-error-msg="errors[0]"
          :error="errors[0]"
          label="Por favor explique brevemente los servicios que solicita, por ejemplo: desarrollo profesional para maestros de matemáticas, servicios de evaluación, etc."
          :maxlength="3000"
          show-char-count
        />
      </validation-provider>

      <div v-else-if="isReadOnly" class="my-2">
        <div class="my-2">
          Por favor explique brevemente los servicios que solicita, por ejemplo:
          desarrollo profesional para maestros de matemáticas, servicios de
          evaluación, etc.
        </div>
        <label class="data-label">
          {{ model.serviceDescription | empty }}
        </label>
      </div>
    </collapse>

    <collapse title="Prioridades a entidades gubernamentales">
      <label class="data-label">
        Indicar si interesa establecer prioridad en la selección de entidades
        gubernamentales para proveer los servicios. Esta prioridad para la
        selección solo tendrá lugar cuando la entidad gubernamental cuente con
        la pericia y capacidad para brindar los servicios que se están
        solicitando, resultando en ahorros y una mayor eficiencia para el DEPR.
      </label>
      <validation-provider v-slot="{ validated }" rules="required">
        <custom-radio
          v-model="hasPrioritiesGovernmentEntities"
          class="custom-radio"
          custom-error-msg="El campo es requerido."
          :disabled="isReadOnly"
          is-inline
          name="prioritiesGovernmentEntities"
          :options="hasPrioritiesGovernmentEntitiesOptions"
        />
        <error
          v-if="
            validated &&
              ![true, false].includes(hasPrioritiesGovernmentEntities?.value)
          "
          message="El campo es requerido."
          variant="no-border"
        />
      </validation-provider>
    </collapse>
    <collapse title="Cuantía estimada">
      <div class="row">
        <div class="col-12 col-md-6">
          <validation-provider
            v-if="!isReadOnly"
            v-slot="{ errors }"
            rules="required|greaterThanZero"
          >
            <label class="data-label">
              Cuantía estimada para los servicios solicitados.
            </label>
            <money
              v-model.lazy.number="estimatedAmount"
              v-bind="moneyOptions"
              class="form-control"
              :class="{ 'was-Invalidated': errors[0] }"
              @input="(val) => (model.estimatedAmount = val)"
            />
            <error v-if="errors[0]" :message="errors[0]" variant="no-border" />
          </validation-provider>

          <div v-else-if="isReadOnly" class="my-2">
            <div>
              Cuantía estimada para los servicios solicitados.
            </div>
            <label class="data-label">
              {{ model.estimatedAmount | price }}
            </label>
          </div>
        </div>
      </div>
    </collapse>
    <collapse title="Estimado del costo">
      <validation-provider
        v-if="!isReadOnly"
        v-slot="{ errors }"
        :custom-messages="{
          required: 'El campo es requerido.',
        }"
        rules="required"
      >
        <custom-text-area
          v-model="model.costEstimateDetails"
          :count-classes="['d-block', 'small-counter']"
          :custom-error-msg="errors[0]"
          :error="errors[0]"
          label="La Unidad Contratante deberá realizar un ejercicio para identificar los posibles costos de cada elemento para los servicios a realizarse con el propósito de obtener una referencia de los precios en el mercado. Deberá en esta sección explicar el ejercicio realizado. Además, podrá incluir anejos de ser necesario. No pueden hacer referencia a ningún nombre de marca o nombre de persona natural o jurídica tanto en los anejos como en este formulario."
          :maxlength="3000"
          show-char-count
        />
      </validation-provider>
      <div v-else-if="isReadOnly" class="my-2">
        <div class="my-2">
          La Unidad Contratante deberá realizar un ejercicio para identificar
          los posibles costos de cada elemento para los servicios a realizarse
          con el propósito de obtener una referencia de los precios en el
          mercado. Deberá en esta sección explicar el ejercicio realizado.
          Además, podrá incluir anejos de ser necesario. No pueden hacer
          referencia a ningún nombre de marca o nombre de persona natural o
          jurídica tanto en los anejos como en este formulario.
        </div>
        <label class="data-label">
          ${{ model.costEstimateDetails | empty }}
        </label>
      </div>
      <slot name="cost-estimate" />
    </collapse>
    <collapse title="Vigencia del contrato">
      <div class="row">
        <div class="col-12 col-md">
          <validation-provider
            v-if="!isReadOnly"
            v-slot="{ errors }"
            :custom-messages="{
              required: 'El campo es requerido.',
            }"
            rules="required"
          >
            <custom-date-picker
              v-model="model.contractStartDate"
              class="w-100"
              :custom-error-msg="errors[0]"
              :error="errors[0]"
              label="Fecha desde:"
              placeholder="DD/MM/YYYY"
              @input="onStartChange"
            />
          </validation-provider>
          <div v-else-if="isReadOnly" class="my-2">
            <div>
              Fecha desde:
            </div>
            <label class="data-label">
              {{ model.contractStartDate | date }}
            </label>
          </div>
        </div>
        <div class="col-12 col-md">
          <validation-provider
            v-if="!isReadOnly"
            v-slot="{ errors }"
            :custom-messages="{
              required: 'El campo es requerido.',
              afterStartDate:
                'La fecha de finalización debe ser posterior a la fecha de inicio.',
            }"
            :rules="{
              required: true,
              afterStartDate: { start: model.contractStartDate },
            }"
          >
            <custom-date-picker
              v-model="model.contractEndDate"
              class="w-100"
              :custom-error-msg="errors[0]"
              :error="errors[0]"
              label="Fecha hasta:"
              :min-date="
                model.contractStartDate
                  ? new Date(model.contractStartDate)
                  : null
              "
              placeholder="DD/MM/YYYY"
            />
          </validation-provider>
          <div v-else-if="isReadOnly">
            <div>
              Fecha hasta:
            </div>
            <label class="data-label">
              {{ model.contractEndDate | date }}
            </label>
          </div>
        </div>
      </div>
    </collapse>
  </div>
</template>

<script>
import { moneyOptions } from '@/utils/objUtils';
import { ValidationProvider, extend } from 'vee-validate';
import { Money } from 'v-money';
import Collapse from '@/components/common/SimpleCollapse.vue';
import CustomTextArea from 'CustomTextArea';
import CustomRadio from 'custom-radio';
import CustomDatePicker from '@/components/common/form/CustomDatePicker';
import Error from 'custom-error';

extend('afterStartDate', {
  params: ['start'],
  validate(value, { start }) {
    if (!value || !start) return true;
    return new Date(value) > new Date(start);
  },
  message: 'La fecha de finalización debe ser posterior a la fecha de inicio.',
});

export default {
  name: 'ExplanatoryFields',
  components: {
    Collapse,
    ValidationProvider,
    CustomTextArea,
    CustomRadio,
    Error,
    Money,
    CustomDatePicker,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    programs: {
      type: Array,
      default: () => [],
    },
    governmentLevels: {
      type: Array,
      default: () => [],
    },
    isReadOnly: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      estimatedAmount: 0,
      moneyOptions,
    };
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    hasPrioritiesGovernmentEntities: {
      get() {
        const value = this.hasPrioritiesGovernmentEntitiesOptions.find(
          (x) => x.value === this.value.hasPrioritiesGovernmentEntities
        );

        return value ?? {};
      },
      set(option) {
        this.$emit('input', {
          ...this.value,
          hasPrioritiesGovernmentEntities: option?.value,
        });
      },
    },
    hasPrioritiesGovernmentEntitiesOptions() {
      return [
        {
          id: 1,
          name: 'yes',
          label: 'Si',
          value: true,
        },
        {
          id: 2,
          name: 'no',
          label: 'No',
          value: false,
        },
      ];
    },
  },
  watch: {
    'model.estimatedAmount': {
      immediate: true,
      handler(newVal) {
        this.estimatedAmount = newVal ?? 0;
      },
    },
  },
  methods: {
    onStartChange() {
      this.model.contractEndDate = null;
    },
  },
};
</script>
<style scoped>
.custom-radio ::v-deep .col-12 {
  padding: 0 !important;
}
.custom-font {
  color: #727272;
  font-size: 14px;
}
::v-deep .custom-datetime-picker-container {
  width: 100% !important;
  input[type='text'] {
    width: 100%;
  }
}
</style>
